import { Fragment, useState } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { IconChevronDown } from "@tabler/icons-react";
import clsx from "clsx";
import Tooltip from "components/Tooltip";
import React from "react";
import InputLabel from "components/InputLabel";

export type SelectOption = {
  id: string | number;
  name: string | React.ReactNode;
  name_de?: string | React.ReactNode;
  value?: string;
  extra?: string;
  tooltip?: string;
};

type CustomSelectProps = {
  options: SelectOption[];
  value?: SelectOption | null;
  onChange: (value: SelectOption | null) => void;
  labelName?: string;
  placeholder?: string;
  clearable?: boolean;
  clearOptionLabel?: string;
  className?: string;
  direction?: "up" | "down";
  defaultValue?: SelectOption | null;
  containerClassName?: string;
};

export default function DropdownSelect({
  options,
  value = null,
  onChange,
  labelName,
  placeholder = "Select an option",
  clearable = false,
  clearOptionLabel = "Clear selection",
  className = "",
  direction = "down",
  defaultValue = null,
  containerClassName = "",
}: CustomSelectProps) {
  const [selected, setSelected] = useState<SelectOption | null>(
    value ?? defaultValue
  );

  React.useEffect(() => {
    setSelected(defaultValue);
  }, [defaultValue]);

  const handleChange = (option: SelectOption | null) => {
    setSelected(option);
    onChange(option);
  };

  return (
    <div className={containerClassName}>
      {labelName && <InputLabel labelName={labelName} />}
      <Listbox value={selected} onChange={handleChange}>
        <div className={`relative ${className}`}>
          <Listbox.Button
            className={clsx(
              "relative w-full cursor-default rounded-lg border border-gray-300 bg-white",
              "p-2 pr-10 text-left shadow-sm focus:outline-none cursor-pointer"
            )}
          >
            {({ open }) => {
              return (
                <>
                  <span className="flex justify-between text-sm font-norma">
                    {selected ? (
                      <span>{selected.name}</span>
                    ) : (
                      <span className="text-gray-500">{placeholder}</span>
                    )}
                  </span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-2">
                    <IconChevronDown
                      className={clsx("h-5 w-5 text-gray-700", {
                        "rotate-180": open,
                      })}
                    />
                  </span>
                </>
              );
            }}
          </Listbox.Button>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options
              className={clsx(
                "absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base",
                "border border-gray-300 focus:outline-none sm:text-sm z-10",
                direction === "up" ? "bottom-full mb-1" : "mt-1"
              )}
            >
              {clearable && selected && (
                <Listbox.Option
                  key="clear"
                  value={null}
                  className={({ active }) =>
                    `relative cursor-pointer select-none py-2 px-4 ${
                      active ? "bg-red-100 text-red-700" : "text-red-700"
                    }`
                  }
                >
                  {() => (
                    <div className="text-sm font-medium">
                      <span>{clearOptionLabel}</span>
                    </div>
                  )}
                </Listbox.Option>
              )}
              {options.map((option) => (
                <Listbox.Option
                  key={option.id}
                  value={option}
                  className={({ active }) =>
                    `relative cursor-pointer select-none py-2 px-4 group/Option ${
                      active ? "bg-indigo-50 text-indigo-700" : "text-gray-900"
                    }`
                  }
                >
                  {() => (
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">{option.name}</span>
                      {option.extra && (
                        <span
                          className={clsx(
                            "text-sm font-normal text-gray-500 group-hover/Option:text-indigo-500 whitespace-nowrap pl-2"
                          )}
                        >
                          {option.extra}
                        </span>
                      )}
                      {option.tooltip && (
                        <Tooltip
                          content={option.tooltip}
                          className="text-gray-500 group-hover/Option:text-indigo-500"
                        ></Tooltip>
                      )}
                    </div>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  );
}
